import React, { useRef, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Radio, Space, Typography } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import {
  getApiBaseUrl, getBackendConnection, getBackendPreset,
  normalizeApiBaseUrl, saveBackendConnection, testBackendConnection
} from '../utils/backendConnection';

export default function BackendConnectionSettings() {
  const [open, setOpen] = useState(false);
  const [connection, setConnection] = useState(getBackendConnection);
  const [result, setResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const probeId = useRef(0);

  const invalidateProbe = () => {
    probeId.current += 1;
    setTesting(false);
    setResult(null);
  };

  const show = () => {
    const current = getBackendConnection();
    setConnection(current);
    form.setFieldsValue(current);
    invalidateProbe();
    setOpen(true);
  };

  const validate = async () => {
    const values = await form.validateFields();
    return { mode: connection.mode, customUrl: values.customUrl || connection.customUrl };
  };

  const test = async () => {
    let draft;
    try { draft = await validate(); } catch { return; }
    const id = ++probeId.current;
    setTesting(true);
    setResult(null);
    try {
      const response = await testBackendConnection(draft);
      if (probeId.current === id) setResult({ type: 'success', message: response.message });
    } catch (error) {
      if (probeId.current === id) setResult({ type: 'error', message: error.message });
    } finally {
      if (probeId.current === id) setTesting(false);
    }
  };

  const save = async () => {
    let draft;
    try { draft = await validate(); } catch { return; }
    setSaving(true);
    try {
      const changed = saveBackendConnection(draft);
      if (changed) window.location.assign('/login');
      else setOpen(false);
    } catch {
      setResult({ type: 'error', message: '无法保存，请允许浏览器使用本地存储后重试' });
    } finally { setSaving(false); }
  };

  const change = (values) => {
    invalidateProbe();
    setConnection(previous => ({ ...previous, ...values }));
    form.setFieldsValue(values);
  };

  let preview;
  try { preview = new URL(getApiBaseUrl(connection), window.location.origin).href; } catch { preview = '填写有效地址后显示'; }

  return (
    <>
      <Button icon={<ApiOutlined />} onClick={show}>后端连接</Button>
      <Modal
        title="后端连接设置"
        open={open}
        onCancel={() => { invalidateProbe(); setOpen(false); }}
        width={560}
        footer={[
          <Button key="cancel" onClick={() => { invalidateProbe(); setOpen(false); }}>取消</Button>,
          <Button key="save" type="primary" loading={saving} disabled={testing} onClick={save}>保存并切换</Button>
        ]}
      >
        <Form form={form} layout="vertical" initialValues={connection} onValuesChange={change}>
          <Form.Item label="连接方式" style={{ marginTop: 20 }}>
            <Radio.Group value={connection.mode} onChange={event => change({ mode: event.target.value })}>
              <Space direction="vertical">
                <Radio value="proxy">默认转发（推荐）</Radio>
                <Radio value="custom">指定后端地址</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {connection.mode === 'proxy' ? (
            <Typography.Paragraph type="secondary">
              适合本地启动和前端隧道访问，前端会自动转发到本机后端。后端隧道可继续用于机器人回调。
            </Typography.Paragraph>
          ) : (
            <>
              <Form.Item
                name="customUrl"
                label="后端地址"
                extra="填写后端根地址即可，系统自动补全 /api。"
                rules={[
                  { required: true, whitespace: true, message: '请输入后端地址' },
                  { validator: (_, value) => {
                    try {
                      if (value && normalizeApiBaseUrl(value) === '/api') throw new Error('请填写完整的 HTTP/HTTPS 后端地址');
                      if (value) normalizeApiBaseUrl(value);
                      return Promise.resolve();
                    } catch (error) { return Promise.reject(error); }
                  } }
                ]}
              >
                <Input placeholder="https://后端隧道域名:端口" autoComplete="off" />
              </Form.Item>
              <Space wrap style={{ marginBottom: 16 }}>
                {window.location.protocol === 'http:' && (
                  <Button size="small" onClick={() => change({ customUrl: 'http://localhost:8000' })}>填入本地地址</Button>
                )}
                {getBackendPreset() && (
                  <Button size="small" onClick={() => change({ customUrl: getBackendPreset() })}>填入当前隧道</Button>
                )}
              </Space>
            </>
          )}
          <Typography.Paragraph style={{ overflowWrap: 'anywhere' }}>
            实际请求地址：<Typography.Text code>{preview}</Typography.Text>
          </Typography.Paragraph>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Button loading={testing} onClick={test}>测试连接</Button>
            {result && <Alert type={result.type} message={result.message} showIcon />}
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              更换后端后需重新登录。设置保存在当前浏览器，不同前端访问地址分别记忆；切回默认转发即可恢复。
            </Typography.Paragraph>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
